import React, { useState, useRef, useEffect } from 'react';
import { FileText, Loader2, AlertCircle } from 'lucide-react';
import { PdfViewerModal } from '@/Components/shared/PdfViewerModal';
import { Document, Page } from '@/Components/shared/react-pdf-lazy';
import './pdf-viewer.css';

interface PdfPreviewCardProps {
  pdfBlob: Blob | null | undefined;
  isLoading: boolean;
  error: boolean;
  title: string;
  documentName?: string;
  headerAction?: React.ReactNode;
  onRetry?: () => void;
}

export const PdfPreviewCard: React.FC<PdfPreviewCardProps> = ({
  pdfBlob,
  isLoading,
  error,
  title,
  documentName,
  headerAction,
  onRetry,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!pdfBlob) {
      setPdfUrl(null);
      return;
    }
    const url = URL.createObjectURL(pdfBlob);
    setPdfUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [pdfBlob]);

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const handlePreviewClick = () => {
    if (pdfUrl) {
      setIsModalOpen(true);
    }
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    return false;
  };

  return (
    <>
      <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-p-4 tw-flex tw-flex-col tw-shadow-sm">
        <div className="tw-flex tw-items-center tw-justify-between tw-mb-3">
          <div className="tw-flex tw-items-center tw-gap-2">
            <FileText className="tw-w-5 tw-h-5 tw-text-affinicar-primary" />
            <h3 className="tw-text-base tw-font-semibold tw-text-gray-900 tw-m-0">
              {title}
            </h3>
          </div>
          {headerAction}
        </div>

        <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-flex-1">
          {isLoading && (
            <div className="tw-text-center tw-py-8">
              <Loader2 className="tw-w-8 tw-h-8 tw-text-affinicar-primary tw-animate-spin tw-mx-auto tw-mb-2" />
              <p className="tw-text-sm tw-text-gray-600">Chargement du PDF...</p>
            </div>
          )}

          {!!error && (
            <div className="tw-text-center tw-py-8 tw-px-4">
              <div className="tw-w-12 tw-h-12 tw-rounded-full tw-bg-red-50 tw-flex tw-items-center tw-justify-center tw-mx-auto tw-mb-3">
                <AlertCircle className="tw-w-6 tw-h-6 tw-text-red-500" />
              </div>
              <p className="tw-text-sm tw-font-medium tw-text-gray-900 tw-mb-1">Impossible de charger le document</p>
              {onRetry && (
                <button
                  onClick={onRetry}
                  className="tw-mt-2 tw-px-4 tw-py-1.5 tw-text-sm tw-font-medium tw-text-affinicar-primary tw-border tw-border-affinicar-primary tw-rounded-lg hover:tw-bg-affinicar-primary/5 tw-transition-colors"
                >
                  Réessayer
                </button>
              )}
              <p className="tw-text-xs tw-text-gray-400 tw-mt-2 tw-mb-0">Si le problème persiste, contactez le support</p>
            </div>
          )}

          {!isLoading && !error && pdfUrl && (
            <div className="tw-w-full" ref={containerRef}>
              <div
                onClick={handlePreviewClick}
                onContextMenu={handleContextMenu}
                className="tw-cursor-pointer tw-transition-all hover:tw-scale-[1.02] hover:tw-shadow-lg tw-relative tw-group"
              >
                <div className="tw-absolute tw-inset-0 tw-bg-affinicar-primary/0 group-hover:tw-bg-affinicar-primary/10 tw-transition-colors tw-rounded tw-flex tw-items-center tw-justify-center tw-z-10">
                  <div className="tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity tw-bg-affinicar-primary tw-text-white tw-px-4 tw-py-2 tw-rounded-md tw-text-sm tw-font-medium tw-shadow-lg">
                    Cliquer pour voir le document complet
                  </div>
                </div>

                <div
                  className="tw-bg-white tw-rounded tw-shadow-md tw-overflow-hidden tw-flex tw-items-center tw-justify-center"
                  style={{ aspectRatio: '1 / 1.414' }}
                >
                  <Document
                    file={pdfUrl}
                    loading={
                      <div className="tw-h-full tw-flex tw-items-center tw-justify-center">
                        <Loader2 className="tw-w-6 tw-h-6 tw-text-affinicar-primary tw-animate-spin" />
                      </div>
                    }
                    error={
                      <div className="tw-h-full tw-flex tw-flex-col tw-items-center tw-justify-center tw-p-4">
                        <div className="tw-w-10 tw-h-10 tw-rounded-full tw-bg-red-50 tw-flex tw-items-center tw-justify-center tw-mb-2">
                          <AlertCircle className="tw-w-5 tw-h-5 tw-text-red-500" />
                        </div>
                        <p className="tw-text-sm tw-text-gray-600 tw-mb-0">Impossible de charger le document</p>
                      </div>
                    }
                  >
                    <Page
                      pageNumber={1}
                      width={containerWidth > 0 ? containerWidth : undefined}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                    />
                  </Document>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <PdfViewerModal
        pdfBlob={pdfBlob || null}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        warrantyName={documentName}
      />
    </>
  );
};
